"use client";

import React, { useEffect, useState, useMemo } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import OTCHistoryDetailsView from "../../MedicalHistory/views/scriptHistoryDetails";
import PatientSignatureModal from "../../../global/view/patientSignature";
import DoctorePrescription from "../../../global/view/doctorePrescription";
import ClaimStatus from "../../../global/view/ClaimStatus";
import { useAuth } from "../../../../context/AuthContext";
import { createProcessPrescription } from "../../../../api/dispensary/script/processScriptApi";
import ErrorModal from "../../../../components/ErrorModal";
import { submitClaim as submitMedikreditClaim } from "../../../../api/medikredit/claimApi";
import { discardRejectedPrescription } from "../../../../api/dispensary/script/processScriptApi";
import ClaimResultModal from "../../../../components/ClaimResultModal";

export default function ScriptPaymentSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const { scriptData, continueAfterMedicalAid, scriptFile } = location.state || {};

  // Fallback: if scriptData is not present in navigation state (for example
  // when returning from the label-configuration route via the browser back
  // button), attempt to hydrate it from the persisted session draft.
  const storedScriptData = (() => {
    try {
      const raw = sessionStorage.getItem("currentScript");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const effectiveScriptData = scriptData || storedScriptData || null;

  const { user, token } = useAuth();

  const [showPatientSig, setShowPatientSig] = useState(false);
  const [pharmacistSig, setPharmacistSig] = useState(null);
  const [patientSig, setPatientSig] = useState(null);
  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [showClaimStatus, setShowClaimStatus] = useState(false);
  const [progressPercent, setProgressPercent] = useState(70);
  const [doctorPreview, setDoctorPreview] = useState(null);
  const [paymentMeta, setPaymentMeta] = useState(null);
  const [validationMessage, setValidationMessage] = useState("");
  const [prescriptionId, setPrescriptionId] = useState(null);
  const [receiptNumber, setReceiptNumber] = useState(null);
  const [localScriptUrl, setLocalScriptUrl] = useState(null);

  // MediKredit claim (fired automatically after a medical-aid script is saved).
  const [showClaimResult, setShowClaimResult] = useState(false);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [claimError, setClaimError] = useState(null);
  const [reversalResult, setReversalResult] = useState(null);
  const [discardingClaim, setDiscardingClaim] = useState(false);

  const todayIsoDate = useMemo(() => {
    try {
      return new Date().toISOString().split("T")[0];
    } catch {
      return undefined;
    }
  }, []);

  const isMedicalAid = (effectiveScriptData?.paymentType || "").toLowerCase() === "medical aid";

  // Create a local object URL for the uploaded script file (if any) so that
  // the embedded history/details view can immediately open the script the
  // user selected on the previous page, before it is persisted in the DB.
  useEffect(() => {
    if (!scriptFile) {
      setLocalScriptUrl(null);
      return;
    }
    const url = URL.createObjectURL(scriptFile);
    setLocalScriptUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore revoke errors
      }
    };
  }, [scriptFile]);

  const scriptDataForView = useMemo(
    () => ({
      ...(effectiveScriptData || {}),
      // Prefer a local object URL if a new file was selected on the form;
      // otherwise fall back to any existing URL that might have been passed
      // in for amend flows.
      scriptDocUrl:
        localScriptUrl ||
        effectiveScriptData?.scriptDocUrl ||
        effectiveScriptData?.docurl ||
        null,
    }),
    [effectiveScriptData, localScriptUrl]
  );

  const getUserIdFromToken = () => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.userid || null;
    } catch {
      return null;
    }
  };

  const handleDoctorComplete = async () => {
    const sd = effectiveScriptData || {};
    const pharmacistid = getUserIdFromToken();
    // Treat this as an amend flow if either the scriptData explicitly
    // marks amendFlow, or if we are on an amend-script route. This
    // prevents creating a brand new prescription when navigating via
    // /customer/dispensary/amend-script/payment-summary.
    const isAmend =
      !!sd.amendFlow ||
      (typeof window !== "undefined" &&
        window.location.pathname.includes("/customer/dispensary/amend-script"));

    const items = Array.isArray(sd.drugs)
      ? sd.drugs
          .filter((m) => m && m.sku && m.saved !== false)
          .map((m) => ({
            sku: m.sku,
            quantity: m.quantity,
            stockQty: m.stockQty,
            toFollowStruck: m.toFollowStruck,
            instructions: m.instructions,
            instructionsOther: m.instructionsOther,
            deliveryOption: m.deliveryOption,
            icd10: m.icd10,
            prescriptionDrugId: m.prescriptionDrugId,
            fromOriginal: m.fromOriginal,
          }))
      : [];

    const pm = paymentMeta || {};

    const payload = {
      profilenumber: sd.profileNo || null,
      patientid: sd.patientId || null,
      practitionerid: sd.practitionerId || null,
      pharmacistid: pharmacistid || null,
      paymenttype: sd.paymentType || null,
      chronic: !!sd.isChronic,
      repeatscript: sd.isRepeat ? (sd.repeatCount || 0) : 0,
      cashpricingoption: sd.scriptType || null,
      processscriptnumber: sd.scriptNumber || sd.scriptNo || null,
      branchcode: sd.branchcode || user?.branchcode || null,
      deliveryoption: pm.deliveryOption || "Collect",
      preferreddate: pm.preferredDate || null,
      preferredtime: pm.preferredTime || null,
      dispensingfee: pm.dispensingFee ?? null,
      gross: pm.grossAmount ?? null,
      items,
      amendFlow: isAmend || false,
      amendOriginalPrescriptionId: isAmend
        ? (sd.originalPrescriptionId || sd.scriptId || sd.scriptNo || null)
        : null,
      // Never send the raw File object in docurl; the backend will derive
      // DOCURL from the uploaded file (req.file) when scriptFile is provided.
      // For amend/new flows we leave docurl null in the JSON payload so the
      // server can safely decide whether to keep or update the stored URL.
      docurl: null,
      amendRemovedDrugs: isAmend && Array.isArray(sd.removedDrugs)
        ? sd.removedDrugs
        : [],
    };

    const resp = await createProcessPrescription(payload, scriptFile);

    if (resp?.success === false) {
      throw new Error(resp?.message || "Failed to create prescription");
    }
    return {
      prescriptionId: resp?.data?.prescriptionid || null,
      receiptNumber: resp?.data?.receiptNumber || null,
      medikreditReversal: resp?.data?.medikreditReversal || null,
    };
  };

  const handleDoctorModalClose = () => {
    setShowDoctorModal(false);
  };

  // TEMP (MediKredit): the old fake claim-status animation is disabled. The real
  // MediKredit claim is submitted from the Script Details page instead. Restore this
  // block to bring back the mock loading screen.
  // useEffect(() => {
  //   if (isMedicalAid && !continueAfterMedicalAid) {
  //     setProgressPercent(70);
  //     setShowClaimStatus(true);
  //   }
  // }, [isMedicalAid, continueAfterMedicalAid]);

  useEffect(() => {
    if (!showClaimStatus) return;
    if (!isMedicalAid) return;
    if (progressPercent >= 100) {
      setShowClaimStatus(false);
      navigate("/customer/dispensary/claim-approved", {
        state: { scriptData: effectiveScriptData, returnToPath: location.pathname },
      });
      return;
    }
    const t = setInterval(() => setProgressPercent((p) => Math.min(100, p + 5)), 500);
    return () => clearInterval(t);
  }, [effectiveScriptData, showClaimStatus, progressPercent, isMedicalAid, navigate, location.pathname]);

  useEffect(() => {
    if (continueAfterMedicalAid) {
      if (location.state?.pharmacistSignature) {
        setPharmacistSig(location.state.pharmacistSignature);
      }
      setShowClaimStatus(false);
      setShowPatientSig(true);
    }
  }, [continueAfterMedicalAid, location.state]);

  // Clear script draft/session keys when leaving both the new-script and payment-summary views
  useEffect(() => {
    return () => {
      try {
        const path = window.location.pathname || "";
        const isNewScriptRoute = path.includes("/customer/dispensary/new-script");
        const isScriptPaymentSummaryRoute = path.includes("/customer/dispensary/script/payment-summary");
        const isLabelConfigurationRoute =
          path.includes("/dispensary/label-configuration") ||
          path.includes("/customer/dispensary/script/payment-summary/label-configuration");

        // When navigating to any label configuration page we want to
        // preserve the currentScript/session data so that using the
        // browser back button returns to the exact same state.
        if (!isNewScriptRoute && !isScriptPaymentSummaryRoute && !isLabelConfigurationRoute) {
          console.log("[ScriptPaymentSummary] Clearing session draft on unmount", { path });
          sessionStorage.removeItem("currentScript");
          sessionStorage.removeItem("cameFromPriceSummary");
          sessionStorage.removeItem("cameFromOTCSummary");
          sessionStorage.removeItem("priceScriptActive");
        }
      } catch {
        // ignore storage errors
      }
    };
  }, []);

  const otcProps = {
    showTotalItems: true,
    invoiceActionLabel: "Sign Invoice",
    rowsPerPage: 1000,
    showPharmacistReadOnly: true,
    showPaymentTypeReadOnly: true,
    showPractitionerReadOnly: true,
    onInvoiceSigned: (sig) => {
      setPharmacistSig(sig);
      // TEMP (MediKredit): skip the fake claim-status animation for medical aid.
      // Restore the isMedicalAid branch below to bring back the mock loading screen.
      // if (isMedicalAid) {
      //   setProgressPercent(70);
      //   setShowClaimStatus(true);
      // } else {
      //   setShowPatientSig(true);
      // }
      setShowPatientSig(true);
    },
  };

  const totals = {
    amount: effectiveScriptData?.amount ?? "",
    vat: effectiveScriptData?.vat ?? "",
    total: paymentMeta?.grossAmount
      ? `R ${paymentMeta.grossAmount}`
      : effectiveScriptData?.total
      ? `R${effectiveScriptData.total.toFixed?.(2) ?? effectiveScriptData.total}`
      : "",
  };

  return (
    <>
      <OTCHistoryDetailsView
        scriptData={scriptDataForView}
        showTotalItems={true}
        rowsPerPage={1000}
        showPharmacistReadOnly={true}
        showPaymentTypeReadOnly={true}
        showPractitionerReadOnly={true}
        invoiceActionLabel="Sign Invoice"
        minPreferredDate={todayIsoDate}
        labelConfigBasePath="/customer/dispensary/script/payment-summary/label-configuration"
        disableRemoteScriptDetails={true}
        onBeforeInvoiceSign={() => {
          if (!paymentMeta?.preferredDate) {
            setValidationMessage("Please select a preferred date before signing the invoice.");
            return false;
          }
          return true;
        }}

        onPreviewDataChange={(data) => {
          setDoctorPreview(data);
          if (data?.paymentMeta) {
            setPaymentMeta(data.paymentMeta);
          }
        }}
        onInvoiceSigned={(sig) => {
          setPharmacistSig(sig);
          // TEMP (MediKredit): skip the fake claim-status animation for medical aid.
          // The real claim is submitted from Script Details. Restore the isMedicalAid
          // branch below to bring back the mock loading screen.
          // if (isMedicalAid) {
          //   setProgressPercent(70);
          //   setShowClaimStatus(true);
          // } else {
          //   setShowPatientSig(true);
          // }
          setShowPatientSig(true);
        }}
      />

      <PatientSignatureModal
        isOpen={showPatientSig}
        onClose={() => setShowPatientSig(false)}
        onConfirm={async (dataUrl) => {
          try {
            setPatientSig(dataUrl);
            const result = await handleDoctorComplete();
            console.log('[ScriptPaymentSummary] handleDoctorComplete result:', result);
            console.log('[ScriptPaymentSummary] result.prescriptionId:', result.prescriptionId);
            console.log('[ScriptPaymentSummary] result.receiptNumber:', result.receiptNumber);
            
            setPrescriptionId(result.prescriptionId);
            setReceiptNumber(result.receiptNumber);

            setShowPatientSig(false);

            // For medical-aid scripts, submit the real MediKredit claim now that the
            // prescription exists, show the result, then continue to the completion popup.
            if (isMedicalAid && result.prescriptionId) {
              setClaimResult(null);
              setClaimError(null);
              setReversalResult(null);
              setClaimSubmitting(true);
              setShowClaimResult(true);

              // Amend flow: show the server-side reversal for the "reversed" banner.
              if (result.medikreditReversal?.reversed) {
                setReversalResult(result.medikreditReversal);
              }

              try {
                const claim = await submitMedikreditClaim(result.prescriptionId);
                setClaimResult(claim);
                const rejected = claim && claim.approved === false && claim.resultText !== "warning";
                if (rejected) {
                  try {
                    await discardRejectedPrescription(result.prescriptionId);
                  } catch (discardErr) {
                    console.error("Failed to discard rejected prescription:", discardErr);
                  }
                }
              } catch (e) {
                setClaimError(e?.response?.data?.message || e.message || "Claim submission failed");
              } finally {
                setClaimSubmitting(false);
              }
            } else {
              setShowDoctorModal(true);
            }
          } catch (e) {
            // If creation fails, keep the modal open so the user can retry or cancel
            console.error("Failed to create prescription on patient confirm:", e);
          }
        }}
        initialSignature={patientSig}
        totals={totals}
      />

      <DoctorePrescription
        isOpen={showDoctorModal}
        onClose={handleDoctorModalClose}
        patientInfo={doctorPreview?.patientInfo}
        prescription={doctorPreview?.prescription}
        pharmacistSignature={pharmacistSig}
        patientSignature={patientSig}
        receiptNumber={receiptNumber}
        prescriptionId={prescriptionId}
        onComplete={undefined}
        totals={totals}
        useCompletedPopup={true}
      />
      
      {/* Debug: Track what's being passed to DoctorePrescription from payment summary */}
      {console.log('[ScriptPaymentSummary] Passing to DoctorePrescription:', {
        receiptNumber,
        prescriptionId,
        doctorPreview: doctorPreview ? 'exists' : 'null'
      })}

      {showClaimResult && (
        <ClaimResultModal
          submitting={claimSubmitting}
          result={claimResult}
          error={claimError}
          reversal={reversalResult}
          discarding={discardingClaim}
          onDiscard={async () => {
            if (!prescriptionId) return;
            setDiscardingClaim(true);
            try {
              await discardRejectedPrescription(prescriptionId);
            } catch (discardErr) {
              console.error("Failed to discard claim:", discardErr);
            } finally {
              setDiscardingClaim(false);
              setShowClaimResult(false);
              navigate("/customer/dispensary/script", { replace: true });
            }
          }}
          onContinue={() => {
            setShowClaimResult(false);
            const rejected =
              claimResult && claimResult.approved === false && claimResult.resultText !== "warning";
            if (rejected) {
              navigate("/customer/dispensary/script", { replace: true });
            } else {
              setShowDoctorModal(true);
            }
          }}
        />
      )}

      <ErrorModal
        isOpen={!!validationMessage}
        onClose={() => setValidationMessage("")}
        message={validationMessage}
      />

      {isMedicalAid && (
        <ClaimStatus
          isOpen={showClaimStatus}
          onClose={() => setShowClaimStatus(false)}
          scriptData={effectiveScriptData}
          claimDetails={{ progressPercent }}
        />
      )}
    </>
  );
}